import { ProctoringSessionSummary } from "@verity/api-client/types";
import { useCallback, useState } from "react";
import apiClient from "../../../../../helpers/api-client";
import { processDisplay, processLink } from "../Timestamps/TimestampLinks";
import { VideoEvent } from "../../../../utilities/useTimestamps";

export default function useSummary() {
	const [sessionSummary, setSessionSummary] = useState<ProctoringSessionSummary | undefined>();
	const [summaryLoading, setSummaryLoading] = useState(true);
	const [summaryShowing, setSummaryShowing] = useState(false);

	const getSummaryData = async (sessionUuid: string) => {
		setSummaryLoading(true);

		try {
			const summary = await apiClient.proctoringSession.getSummary(sessionUuid);
			setSessionSummary(summary);
		} catch (error) {
			console.error("Failed to fetch summary", error);
		} finally {
			setSummaryLoading(false);
		}
	};

	const summaryExists = !!sessionSummary && Object.keys(sessionSummary).length > 0;

	const showSummary = () => setSummaryShowing(true);

	const hideSummary = () => setSummaryShowing(false);

	const resetSummary = () => {
		setSessionSummary(undefined);
		setSummaryShowing(false);
	};

	const convertAiSummaryEvents = (events: VideoEvent[], timeOffset: number) =>
		events.map((ev) => {
			const adjustedTime = Number(Math.max(ev.time - timeOffset / 1000, 0).toFixed(2)); // Sync passed time with what's shown in the events table
			const eventLink = processLink(ev);
			const eventDescription = eventLink ? `${processDisplay(ev)} ${eventLink}` : processDisplay(ev);
			return {
				name: ev.name,
				time: adjustedTime,
				flag_level: ev.flag_level,
				description: eventDescription, // Plain language description of the event
				resolved: ev.resolved,
				comment: ev.comment ?? undefined,
			};
		});

	const generateAiSummary = async (sessionUuid: string, events: VideoEvent[], timeOffset: number) => {
		if (summaryExists) return;

		try {
			const generatedSummary = await apiClient.proctoringSession.generateAiSummary(
				sessionUuid,
				convertAiSummaryEvents(events, timeOffset),
			);
			setSessionSummary(generatedSummary);
		} catch (err) {
			console.error(err);
		}
	};

	const getDefaultSummary = useCallback(async (sessionUuid: string, events: VideoEvent[], timeOffset: number) => {
		return await apiClient.proctoringSession.generateDefaultLiveProctorSummary(
			sessionUuid,
			convertAiSummaryEvents(events, timeOffset),
		);
	}, []);

	const saveSummary = useCallback(async (sessionUuid: string, summary: string) => {
		const session = await apiClient.proctoringSession.updateLiveProctorSummary(sessionUuid, summary);
		setSessionSummary(session);
	}, []);

	return {
		sessionSummary,
		summaryLoading,
		summaryShowing,
		getSummaryData,
		summaryExists,
		showSummary,
		hideSummary,
		resetSummary,
		generateAiSummary,
		getDefaultSummary,
		saveSummary,
	};
}
